import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DataListRelationship } from "src/app/shared/models/data-list";
import {
  CreateUpdateDisputePartyBaseDto,
  DisputeCaseBaseDto,
  DisputePartyBaseDto,
} from "src/app/shared/models/dispute-resolution/dispute-base-models";
import { AddEditPartyInformationComponent } from "../add-edit-party-information/add-edit-party-information.component";
import { AuthService } from "src/app/auth/auth.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-parties-list",
  templateUrl: "./parties-list.component.html",
  styleUrls: ["./parties-list.component.scss"],
  standalone: false,
})
export class PartiesListComponent implements OnInit, OnDestroy {
  @Input() disputeCase: DisputeCaseBaseDto;
  @Input() parties: CreateUpdateDisputePartyBaseDto[] = [];
  @Input() showSurveyData: boolean = false;
  @Input() relationshipOptions: DataListRelationship[] = [];
  @Input() filerLabel: string = "Filer";
  @Output() partiesChange = new EventEmitter<
    CreateUpdateDisputePartyBaseDto[]
  >();

  displayedColumns: string[] = ["relationship", "nameAddress", "actions"];
  footerRowColumns = ["surveyLink", "surveyStatus"];

  private subscriptions = new Subscription();

  isFooterRow = (_index: number, _row: Record<string, unknown>) =>
    !!_row?.tenantSurveyId && this.showSurveyData;

  get isPublicUser(): boolean {
    return this.authService.isPublicUser;
  }

  constructor(
    private dialog: MatDialog,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.parties = this.parties.filter((p) => !p.isChild);

    // Set displayedColumns based on user type
    if (this.isPublicUser) {
      this.displayedColumns = ["relationship", "nameAddress"];
    } else {
      this.displayedColumns = ["relationship", "nameAddress", "actions"];
    }

    this.sortParties();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getRelationshipLabel(party: DisputePartyBaseDto): string | undefined {
    if (party.isFiler) {
      return this.filerLabel;
    } else if (party.isRespondent) {
      return "Responder";
    } else if (
      !this.relationshipOptions ||
      this.relationshipOptions.length === 0
    ) {
      // Relationship options not loaded yet
      return undefined;
    } else {
      const relationship = this.relationshipOptions.find(
        (r) => r.id === party.relationshipId
      );
      if (relationship) {
        return (
          relationship.label +
          (party.relationshipOther ? ` (${party.relationshipOther})` : "")
        );
      }
    }
  }

  addParty() {
    const dialogRef = this.dialog.open(AddEditPartyInformationComponent, {
      width: "1100px",
      data: {
        disputeCase: this.disputeCase,
        party: null,
        relationshipOptions: this.relationshipOptions,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.parties = [...this.parties, { ...result }];
        this.sortParties();
        this.partiesChange.emit(this.parties);
      }
    });
  }

  editParty(party: DisputePartyBaseDto) {
    const dialogRef = this.dialog.open(AddEditPartyInformationComponent, {
      width: "1100px",
      data: {
        disputeCase: this.disputeCase,
        party: { ...party },
        relationshipOptions: this.relationshipOptions,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (!result.id) {
          // Editing a newly created party (no id): match by reference
          const idx = this.parties.indexOf(party);
          if (idx !== -1) {
            this.parties = this.parties.map((p, i) =>
              i === idx ? { ...p, ...result } : p
            );
          }
        } else {
          // Editing an existing party (with id): match by id
          this.parties = this.parties.map((p) =>
            p.id && result.id && p.id === result.id ? { ...p, ...result } : p
          );
        }
        this.sortParties();
        this.partiesChange.emit(this.parties);
      }
    });
  }

  private sortParties() {
    if (!this.parties || !this.relationshipOptions) return;

    const getLabel = (party: DisputePartyBaseDto) => {
      if (party.isFiler) return this.filerLabel;
      if (party.isRespondent) return "Responder";
      const rel = this.relationshipOptions.find(
        (r) => r.id === party.relationshipId
      );
      return rel ? rel.label : "";
    };

    this.parties = [
      ...this.parties.filter((p) => p.isFiler),
      ...this.parties.filter((p) => p.isRespondent && !p.isFiler),
      ...this.parties
        .filter((p) => !p.isFiler && !p.isRespondent)
        .sort((a, b) => getLabel(a).localeCompare(getLabel(b))),
    ];
  }
}
